import { useImmerAtom } from 'jotai-immer';
import { useSetImmerAtom } from 'jotai-immer';
import { cpOrderInvoicesTableStateAtom, cpOrderInvoicesTableViewAtom } from '../cp_order_invoices_list_atoms';
import { cpOrderInvoicesSearchFormAtom } from './cp_order_invoices_search_atoms';
import { CpOrderInvoicesSearchModel } from './cp_order_invoices_search_types';
import { useCallback, useEffect } from 'react';
import { useCpOrderInvoicesStore } from '../pagehooks/useCpOrderInvoicesStore';
import utilSvc from '../../../../core/utilSvc';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { CP_ORDER_INVOICES_LIST_FORM_KEY, FEATURE_NAME, getAffs } from '../../cp_order_invoices_const';
import tableViewRepo from '../../../../core/repo/viewStateRepo';
import { cleanCpOrderInvoicesSearchPayload } from './cleanCpOrderInvoicesSearchPayload';
import { stripAwayFormErrors } from '../../../../core/table/utils/stripAwayFormErrors';
import { getCpOrderInvoicesInitialView } from '../table/getCpOrderInvoicesInitialView';
import { getCpOrderInvoicesProgAdminView } from '../table/getCpOrderInvoicesProgAdminView';
import { cpOrderInvoicesListColumnSpec } from '../table/CpOrderInvoicesListTableSpec';
import { tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import { useAtomValue } from 'jotai';
import { cpOrderInvoicesUserAffListAtom } from '../cp_order_invoices_list_atoms';

export type UseCpOrderInvoicesSearchStateReturn = {
    state: CpOrderInvoicesSearchModel;
    setProperty: (key: keyof CpOrderInvoicesSearchModel, val: any) => void;
    toggleChecked: (key: keyof CpOrderInvoicesSearchModel, val: boolean) => void;
    handleSearch: () => void;
    syncDateRange: (start: Date, end: Date) => void;
};

const colSpec = cpOrderInvoicesListColumnSpec();
// Build default view spec
const defaultView = getCpOrderInvoicesInitialView();
const programAdminView = getCpOrderInvoicesProgAdminView();

export const useCpOrderInvoicesSearchState = (): UseCpOrderInvoicesSearchStateReturn => {
    const [state, _setState] = useImmerAtom(cpOrderInvoicesSearchFormAtom);
    const { getList } = useCpOrderInvoicesStore();
    const userAffList = useAtomValue(cpOrderInvoicesUserAffListAtom);

    const setTableState = useSetImmerAtom(cpOrderInvoicesTableStateAtom);
    const setView = useSetImmerAtom(cpOrderInvoicesTableViewAtom);

    useEffect(() => {
        setTableState(draft => {
            // Set views and currentViewId
            const views = [defaultView, programAdminView];
            const currentViewId = state.ReportType?.val === 'programAdmin' ? programAdminView.key : defaultView.key;

            const currentView = tblSpecLoadView(
                currentViewId,
                views,
                colSpec,
                state.ReportType?.val === 'programAdmin'
                    ? getCpOrderInvoicesProgAdminView
                    : getCpOrderInvoicesInitialView,
            );

            draft.views = views;
            draft.currentViewId = currentViewId;

            setView(() => currentView);
        });
    }, [state.ReportType?.val, setTableState, setView]);

    // Syncs the computed date range into the form state
    const syncDateRange = useCallback(
        (start: Date, end: Date) => {
            const format = (d: Date): string =>
                d
                    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                    : '';
            _setState(draft => {
                draft.StartDate.val = format(start);
                draft.EndDate.val = format(end);
            });
        },
        [_setState],
    );

    const setProperty = useCallback(
        (key: keyof CpOrderInvoicesSearchModel, val: any): void => {
            _setState(draft => {
                // Use centralized user context from page state atom
                if (key === 'ReportType') {
                    draft.ReportType.val = val;
                    draft.RX.val = [];
                    if (val === 'facility') {
                        draft.ProgramAdmin.val = 'N/A';
                    } else {
                        draft.CE.val = 'N/A';
                    }
                } else if (key === 'CE') {
                    let affs = getAffs(userAffList, FEATURE_NAME);
                    affs = utilSvc.aff.findCe(affs, val);

                    const rxList = utilSvc.aff.rxList(affs);
                    const rxOptions = rxList.map(rx => ({ lbl: rx, val: rx }));

                    draft.CE.val = val;
                    draft.RX.val = rxList;
                    draft.RX.options = rxOptions;
                    // Clear any remaining error on selection
                    draft.CE.err = '';
                } else if (key === 'ProgramAdmin') {
                    // Step 1: Raw affiliations
                    let affs = utilSvc.aff.findStatus(userAffList || []);
                    affs = utilSvc.aff.findRxType(affs, ['DISPENSING', 'CENTRALFILL']);
                    affs = utilSvc.aff.findRxModel(affs, utilSvc.aff.rxModelsByFeature(FEATURE_NAME));
                    const rxAffs = affs.filter(a => a.ProgAdmin === val);
                    const rxList = utilSvc.aff.rxList(rxAffs);
                    const rxOptions = rxList.map(rx => ({ lbl: rx, val: rx }));

                    draft.ProgramAdmin.val = val;
                    draft.RX.val = rxList;
                    draft.RX.options = rxOptions;
                    // Clear any remaining error on selection
                    draft.ProgramAdmin.err = '';
                } else if (key === 'RX') {
                    draft[key].val = val;
                    draft[key].err = '';
                } else {
                    draft[key].val = val;
                }
            });
        },
        [_setState, userAffList],
    );

    const toggleChecked = useCallback(
        (key: keyof CpOrderInvoicesSearchModel, val: boolean): void => {
            _setState(draft => {
                (draft[key] as any).checked = val;
            });
        },
        [_setState],
    );

    const validate = useCallback((): boolean => {
        if (state.ReportType.val === 'facility' && state.CE.val === 'N/A') {
            _setState(draft => {
                draft.CE.err = 'Facility selection is required.';
            });
            return false;
        }
        if (state.ReportType.val === 'programAdmin' && state.ProgramAdmin.val === 'N/A') {
            _setState(draft => {
                draft.ProgramAdmin.err = 'Program Admin selection is required.';
            });
            return false;
        }
        if (state.RX.val?.length === 0 || !state.RX.val) {
            _setState(draft => {
                draft.RX.err = 'At least one Pharmacy selection is required.';
            });
            return false;
        }
        if (state.RX.val?.length !== 0) {
            _setState(draft => {
                draft.RX.err = '';
            });
            return true;
        }
        return true;
    }, [state.RX, state.CE, state.ProgramAdmin, state.ReportType, _setState]);

    const submit = (): void => {
        if (!validate()) {
            ngDelegate.ok('Cannot Search', 'Check properties');
            return;
        } else {
            handleSearch();
        }
    };

    const handleSearch = useCallback(() => {
        const cleanedState = cleanCpOrderInvoicesSearchPayload(state);
        // Save form viewstate to localStorage (match Provider behavior)
        tableViewRepo.setForm(CP_ORDER_INVOICES_LIST_FORM_KEY, stripAwayFormErrors(cleanedState));
        // Delegate to store's getList, which handles API, state, and data atom updates
        getList(state);
    }, [getList, state]);

    return {
        state,
        setProperty,
        toggleChecked,
        handleSearch: submit,
        syncDateRange,
    };
};
